<template>
  <div class="main-container">
    <!-- Menu Samping untuk Datalog -->
    <div class="datalog-menu" :class="{ 'menu-visible': isMenuVisible }">
      <div v-if="isMenuVisible" class="datalog-content">
        <h5 class="border-bottom pb-2 mb-3">Datalog Trail (Device: {{ selectedDeviceId || 'N/A' }})</h5>
        <div class="date-filter-section mb-3">

          <!-- V-Calendar Date Picker -->
          <div class="mx-2 mb-3">
            <span class="d-md-block text-muted small text-left font-weight-bold">Select Range</span>
            <date-time-picker v-model="dateRange" is-range :masks="masks" />
          </div>

          <!-- Aggregation Filters -->
          <div class="form-row">
            <div class="form-group col-md-6">
              <label for="aggUnit">Aggregate Unit</label>
              <select id="aggUnit" class="form-control" v-model="aggregatedUnit">
                <option>MINUTE</option>
                <option>HOUR</option>
                <option>DAY</option>
              </select>
            </div>
            <div class="form-group col-md-6">
              <label for="aggLength">Aggregate Length</label>
              <NumberInput id="aggLength" class="form-control" v-model="aggregatedLength" :decimals="0" :min="1" />
            </div>
          </div>

          <button class="btn btn-mantraz btn-block" @click="applyDateFilter" :disabled="isLoading">
            <wave-loader v-if="isLoading" size="sm" mono />
            Apply Filter
          </button>
        </div>
        <div class="table-responsive">
          <table class="table table-striped table-sm">
            <thead>
              <tr>
                <th>Datetime</th>
                <th>Lat</th>
                <th>Lon</th>
                <th>Speed</th>
                <th>Angle</th>
                <th>Sats</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="seriesData.length === 0">
                <td colspan="6" class="text-center">No data available.</td>
              </tr>
              <tr v-for="item in seriesData" :key="item.id">
                <td>{{ formatToBrowserTime(item.datetime) }}</td>
                <td>{{ $fmt(item.latitude, 4) }}</td>
                <td>{{ $fmt(item.longitude, 4) }}</td>
                <td>{{ item.speed }}</td>
                <td>{{ item.angle }}</td>
                <td>{{ item.satellites }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Kontainer Peta -->
    <div id="map-section" class="map-container" :class="{ 'map-narrow': isMenuVisible }">
      <div id="map" style="height: 100%; width: 100%;"></div>
      <div v-if="isLoading && !isMenuVisible" class="d-flex justify-content-center align-items-center"
        style="position: absolute; top: 0; left: 0; height: 100%; width: 100%; background: rgba(255,255,255,0.7); z-index: 99999;">
        <wave-loader />
      </div>
    </div>
  </div>
</template>

<script>
import { fromApiDatetime, DISPLAY_DATETIME } from '@/util/apiDatetime';
/* eslint-disable no-undef, new-cap */
import TeltonikaSvc from '@/services/TeltonikaSvc';
import { formatDateTime } from '@/util/date';
import carIcon from '@/assets/icons/car-icon.png';
import DateTimePicker from '@/components/directives/DateTimePicker.vue';
import NumberInput from '@/components/directives/NumberInput.vue';
import { formatNum } from '@/util/number';

export default {
  name: 'TeltonikaMap',
  components: {
    NumberInput,
    DateTimePicker,
  },
  data() {
    return {
      isLoading: false,
      isMenuVisible: false,
      map: null,
      deviceIds: [866344059402631, 350612078298856], // Array of device IDs
      markers: {}, // Object to store markers by deviceId
      polylineLayers: {}, // Object to store polyline layers by deviceId
      activeDeviceTrail: null, // Tracks which device's trail is visible
      initialLastData: {}, // Object to store initial last data for each device
      seriesData: [], // This will now hold data for the *selected* device for the table
      selectedDeviceId: null, // Tracks which device's datalog is currently displayed
      dateRange: {
        start: new Date(new Date().getTime() - (24 * 60 * 60 * 1000)),
        end: new Date(),
      },
      masks: 'YYYY-MM-DD HH:mm',
      aggregatedUnit: 'MINUTE',
      aggregatedLength: 15,
    };
  },
  methods: {
    loadCss(url, id) {
      return new Promise((resolve, reject) => {
        if (document.getElementById(id)) {
          resolve();
          return;
        }
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = url;
        link.onload = () => resolve();
        link.onerror = () => reject(new Error(`Gagal memuat CSS: ${url}`));
        document.head.appendChild(link);
      });
    },

    unloadCss(id) {
      const link = document.getElementById(id);
      if (link) link.parentNode.removeChild(link);
    },

    formatToBrowserTime(datetimeString) {
      if (!datetimeString) return 'N/A';
      const date = fromApiDatetime(datetimeString).toDate();
      return formatDateTime(date, DISPLAY_DATETIME);
    },

    createMarkerPopup(data) {
      const localDatetime = this.formatToBrowserTime(data.datetime);
      const popupContent = `
        <div class="leaflet-popup-content" style="width: 280px; padding: 10px; font-family: inherit; font-size: 14px;">
          <h5 style="margin-top: 0; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">Device: ${data.deviceId || 'N/A'}</h5>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
            <tr><td style="padding: 2px 0; font-weight: bold;">Datetime</td><td style="padding: 2px 0;">: ${localDatetime}</td></tr>
            <tr><td style="padding: 2px 0; font-weight: bold;">Latitude</td><td style="padding: 2px 0;">: ${formatNum(data.latitude, 6) || 'N/A'}</td></tr>
            <tr><td style="padding: 2px 0; font-weight: bold;">Longitude</td><td style="padding: 2px 0;">: ${formatNum(data.longitude, 6) || 'N/A'}</td></tr>
            <tr><td style="padding: 2px 0; font-weight: bold;">Altitude</td><td style="padding: 2px 0;">: ${data.altitude || 'N/A'} m</td></tr>
            <tr><td style="padding: 2px 0; font-weight: bold;">Angle</td><td style="padding: 2px 0;">: ${data.angle || 'N/A'} °</td></tr>
            <tr><td style="padding: 2px 0; font-weight: bold;">Speed</td><td style="padding: 2px 0;">: ${data.speed || 'N/A'} km/h</td></tr>
            <tr><td style="padding: 2px 0; font-weight: bold;">Satellites</td><td style="padding: 2px 0;">: ${data.satellites || 'N/A'}</td></tr>
          </table>
          <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #ddd; padding-top: 10px;">
            <div class="polyline-toggle-container">
              <label class="switch">
                <input type="checkbox" id="trail-switch-${data.deviceId}" ${this.activeDeviceTrail === data.deviceId ? 'checked' : ''}>
                <span class="slider round"></span>
              </label>
              <span class="toggle-label">Show Trail</span>
            </div>
            <button id="datalog-btn-${data.deviceId}" class="btn btn-mantraz btn-sm">View Datalog</button>
          </div>
        </div>
      `;
      // Rebind popup events each time to ensure they refer to the correct device ID
      if (this.markers[data.deviceId]) {
        this.markers[data.deviceId].bindPopup(popupContent).on('popupopen', () => {
          this.$nextTick(() => {
            document.getElementById(`datalog-btn-${data.deviceId}`).onclick = () => this.handleDatalogClick(data.deviceId);
            document.getElementById(`trail-switch-${data.deviceId}`).onchange = (e) => this.handleTrailToggle(data.deviceId, e.target.checked);
          });
        });
      }
    },

    async handleTrailToggle(deviceId, isVisible) {
      if (isVisible) {
        // Hide previously active trail if any
        if (this.activeDeviceTrail && this.activeDeviceTrail !== deviceId) {
          if (this.polylineLayers[this.activeDeviceTrail]) {
            this.map.removeLayer(this.polylineLayers[this.activeDeviceTrail]);
            // Uncheck the switch for the previously active trail
            const prevSwitch = document.getElementById(`trail-switch-${this.activeDeviceTrail}`);
            if (prevSwitch) prevSwitch.checked = false;
          }
        }
        this.activeDeviceTrail = deviceId;

        if (this.polylineLayers[deviceId]) {
          this.map.addLayer(this.polylineLayers[deviceId]);
        } else {
          // If polyline not yet fetched, fetch it
          if (!this.initialLastData[deviceId]) {
            console.warn('Initial data not available for this device.');
            this.activeDeviceTrail = null;
            // Uncheck the switch if data is not available
            this.$nextTick(() => {
              const switchElement = document.getElementById(`trail-switch-${deviceId}`);
              if (switchElement) switchElement.checked = false;
            });
            return;
          }
          const endDate = new Date(this.initialLastData[deviceId].timestamp * 1000);
          const startDate = new Date(endDate.getTime() - (24 * 60 * 60 * 1000));
          await this.fetchSeriesData(startDate.getTime() / 1000, endDate.getTime() / 1000, deviceId);
        }
      } else {
        if (this.polylineLayers[deviceId]) {
          this.map.removeLayer(this.polylineLayers[deviceId]);
        }
        if (this.activeDeviceTrail === deviceId) {
          this.activeDeviceTrail = null;
        }
      }
    },

    async handleDatalogClick(deviceId) {
      this.selectedDeviceId = deviceId;
      this.isMenuVisible = true;
      this.map.closePopup();
      this.$nextTick(() => this.map.invalidateSize());

      if (this.initialLastData[deviceId]) {
        const endDate = new Date(this.initialLastData[deviceId].timestamp * 1000);
        const startDate = new Date(endDate.getTime() - (24 * 60 * 60 * 1000));
        this.dateRange = { start: startDate, end: endDate };
      }

      await this.applyDateFilter();
    },

    async applyDateFilter() {
      if (!this.selectedDeviceId) {
        console.warn('Please select a device first by clicking on a marker.');
        return;
      }
      if (!this.dateRange || !this.dateRange.start || !this.dateRange.end) {
        console.warn('Please select a valid date range.');
        return;
      }
      const startTimestamp = this.dateRange.start.getTime() / 1000;
      const endTimestamp = this.dateRange.end.getTime() / 1000;
      await this.fetchSeriesData(startTimestamp, endTimestamp, this.selectedDeviceId);
    },

    async fetchSeriesData(startTimestamp, endTimestamp, deviceId) {
      this.isLoading = true;
      try {
        const seriesResponse = await TeltonikaSvc.getPeriodData(startTimestamp, endTimestamp, this.aggregatedUnit, [deviceId], 7, this.aggregatedLength);
        this.seriesData = (seriesResponse?.data?.series || []).sort((a, b) => b.timestamp - a.timestamp);
        this.updateMapWithSeriesData(deviceId, this.seriesData);
      } catch (error) {
        console.error(`Error fetching series data for device ${deviceId}:`, error);
        this.seriesData = [];
      } finally {
        this.isLoading = false;
      }
    },

    updateMapWithSeriesData(deviceId, seriesData) {
      // Remove existing polyline for this device if it exists
      if (this.polylineLayers[deviceId]) {
        this.map.removeLayer(this.polylineLayers[deviceId]);
      }

      const validPoints = seriesData.filter((p) => p.latitude !== 0 && p.longitude !== 0);
      if (validPoints.length === 0) {
        // If no valid points for trail, ensure marker is still there and popup is updated
        if (this.markers[deviceId] && this.initialLastData[deviceId]) {
          this.createMarkerPopup(this.initialLastData[deviceId]);
        }
        return;
      }

      const latlngs = validPoints.map((p) => [p.latitude, p.longitude]).reverse();
      this.polylineLayers[deviceId] = L.polyline(latlngs, { color: 'blue', weight: 3, opacity: 0.7 });

      // Only add to map if this is the active trail
      if (this.activeDeviceTrail === deviceId) {
        this.map.addLayer(this.polylineLayers[deviceId]);
      }

      // Update marker position and popup for the latest point if it's the selected device
      const lastPoint = validPoints[0];
      if (this.markers[deviceId]) {
        this.markers[deviceId].setLatLng([lastPoint.latitude, lastPoint.longitude]);
        this.markers[deviceId].setRotationAngle(lastPoint.angle + 10);
      } else {
        // This case should ideally not happen if initial data is fetched correctly
        // but added for robustness.
        this.markers[deviceId] = L.marker([lastPoint.latitude, lastPoint.longitude], {
          icon: L.icon({ iconUrl: carIcon, iconSize: [76, 76] }),
          rotationAngle: lastPoint.angle + 10,
          rotationOrigin: 'center',
        }).addTo(this.map);
      }
      this.createMarkerPopup(lastPoint);

      // Adjust map view to fit the polyline bounds
      if (this.polylineLayers[deviceId] && this.activeDeviceTrail === deviceId) {
        this.map.fitBounds(this.polylineLayers[deviceId].getBounds(), { padding: [50, 50] });
      } else if (this.markers[deviceId]) {
        this.map.setView([lastPoint.latitude, lastPoint.longitude], 18);
      }
    },

    async fetchInitialData() {
      this.isLoading = true;
      try {
        // Mengirimkan array deviceIds ke getLastData
        const responseData = await TeltonikaSvc.getLastData(this.deviceIds);
        // Diasumsikan responseData.data.series adalah array data terakhir untuk semua perangkat yang diminta
        const fetchedSeries = responseData?.data?.series || [];

        // Hapus marker dan data awal yang ada
        Object.keys(this.markers).forEach((deviceId) => {
          if (this.markers[deviceId]) {
            this.map.removeLayer(this.markers[deviceId]);
          }
        });
        this.markers = {};
        this.initialLastData = {};

        let firstDeviceLatlng = null;
        fetchedSeries.forEach((data) => {
          const deviceId = data.deviceId;
          this.initialLastData[deviceId] = data;

          if (data.latitude !== 0 && data.longitude !== 0) {
            this.markers[deviceId] = L.marker([data.latitude, data.longitude], {
              icon: L.icon({ iconUrl: carIcon, iconSize: [76, 76] }),
              rotationAngle: data.angle + 10,
              rotationOrigin: 'center',
            }).addTo(this.map);
            this.createMarkerPopup(data); // Bind popup for the newly created marker

            if (!firstDeviceLatlng) {
              firstDeviceLatlng = [data.latitude, data.longitude];
            }
          }
        });

        if (firstDeviceLatlng) {
          this.map.setView(firstDeviceLatlng, 13); // Set view to the first available device's location
        } else {
          // Fallback if no valid device data
          this.map.setView([-6.200000, 106.816666], 13); // Default view
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    try {
      // Load Leaflet CSS
      await this.loadCss('/style/leaflet.css', 'leaflet-style');
      // Load Leaflet Rotated Marker JS
      await new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = '/script/leaflet.rotatedMarker.js';
        script.onload = resolve;
        document.head.appendChild(script);
      });

      const parent = document.getElementById('map-section');
      let mapContainer = document.getElementById('map');
      if (mapContainer) mapContainer.remove(); // Remove existing map div if any

      mapContainer = document.createElement('div');
      mapContainer.id = 'map';
      parent.appendChild(mapContainer);

      this.map = L.map(mapContainer).setView([-6.200000, 106.816666], 13); // Default view
      L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&gl=id&hl=id', {
        attribution: 'Map data © Google Maps',
      }).addTo(this.map);

      await this.fetchInitialData();
    } catch (error) {
      console.error('Gagal memuat peta:', error);
      this.isLoading = false;
    }
  },

  beforeDestroy() {
    this.unloadCss('leaflet-style');
    // Remove Leaflet Rotated Marker JS if necessary (optional, as it's usually fine to leave it)
    const rotatedMarkerScript = document.querySelector('script[src="/script/leaflet.rotatedMarker.js"]');
    if (rotatedMarkerScript) rotatedMarkerScript.remove();

    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    // Clean up all markers and polylines
    Object.keys(this.markers).forEach((deviceId) => {
      if (this.markers[deviceId]) this.map.removeLayer(this.markers[deviceId]);
    });
    Object.keys(this.polylineLayers).forEach((deviceId) => {
      if (this.polylineLayers[deviceId]) this.map.removeLayer(this.polylineLayers[deviceId]);
    });
  },
};
</script>

<style scoped>
.main-container {
  display: flex;
  height: calc(100vh - 70px);
  width: 100%;
}

.datalog-menu {
  width: 0;
  background-color: #f8f9fa;
  overflow: hidden;
  transition: width 0.5s;
  flex-shrink: 0;
  height: 100%;
}

.datalog-menu.menu-visible {
  width: 40%;
  border-right: 1px solid #ddd;
}

.datalog-content {
  padding: 15px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.table-responsive {
  flex-grow: 1;
  overflow-y: auto;
}

.map-container {
  width: 100%;
  height: 100%;
  transition: width 0.5s;
}

.map-container.map-narrow {
  width: 60%;
}

/* Styling untuk toggle switch */
.polyline-toggle-container {
  display: flex;
  align-items: center;
}

.toggle-label {
  margin-left: 8px;
  font-family: inherit;
  font-size: 14px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  margin-bottom: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: .4s;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  -webkit-transition: .4s;
  transition: .4s;
}

input:checked+.slider {
  background-color: #2196F3;
}

input:focus+.slider {
  box-shadow: 0 0 1px #2196F3;
}

input:checked+.slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}

.slider.round {
  border-radius: 24px;
}

.slider.round:before {
  border-radius: 50%;
}
</style>
